import { MoreVert } from "@mui/icons-material";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { showPrompt } from "@samdesk/components/Prompt";
import useCreateDashboardTab from "api/mutations/useCreateDashboardTab";
import useDeleteDashboardTab from "api/mutations/useDeleteDashboardTab";
import useUpdateDashboardTab from "api/mutations/useUpdateDashboardTab";
import useGetAccount from "api/queries/useGetAccount";
import useGetDashboard from "api/queries/useGetDashboard";
import { Tab } from "autoGenSubTypes";
import { CreateDashboardTabInput, DashboardTabType } from "gql/graphql";
import { useState } from "react";
import theme from "theme";
import useNavigateWithAccount from "utils/useNavigateWithAccount";
import useSnackbar from "utils/useSnackbar";
import useNavigateAfterTabDelete from "./useNavigateAfterTabDelete";

type ColumnsTabFields = Extract<
	Tab,
	{ __typename: "ColumnsTab" }
>["columns_tab_data"];

interface Props {
	tabData: ColumnsTabFields;
	tabKey: string;
}

export const ColumnsTabMenu = ({ tabKey, tabData }: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const navigateAfterTabDelete = useNavigateAfterTabDelete();
	const { handleError } = useSnackbar();

	const navigateWithAccount = useNavigateWithAccount();

	const { mutateAsync: deleteDashboardTab } = useDeleteDashboardTab();
	const { mutateAsync: createDashboardTab } = useCreateDashboardTab();
	const { mutateAsync: updateDashboardTab } = useUpdateDashboardTab();

	const { data: dashboard } = useGetDashboard();
	const { data: account } = useGetAccount();

	const columnsTabLimit = account?.settings.max_dashboard_columns_tabs ?? 0;
	const atColumnsTabLimit =
		(dashboard?.tabs.filter((tab) => tab.__typename === "ColumnsTab").length ??
			0) >= columnsTabLimit;

	const handleClickRename = async () => {
		setAnchorEl(null);

		const { state, value: newTabName } = await showPrompt({
			promptType: "input",
			title: "Rename Tab",
		});

		if (state === "ok" && newTabName) {
			try {
				const response = await updateDashboardTab({
					key: tabKey,
					tab: {
						columns_tab_data: {
							name: newTabName,
						},
						type: DashboardTabType.COLUMNS,
					},
				});

				if (response.error) {
					handleError(response.error);
				}
			} catch (error) {
				handleError(error);
			}
		}
	};

	const handleSubmitDuplicate = async () => {
		const newColumnsTab: CreateDashboardTabInput = {
			columns_tab_data: {
				name: tabData.name,
				columns: tabData.columns.map((column) => ({
					panes: column.panes.map((pane) => ({
						sound: {
							enabled: pane.sound.enabled,
							name: pane.sound.name,
						},
						unreadOnly: pane.unreadOnly,
						timeWindowSeconds: pane.timeWindowSeconds,
						streamIds: pane.streamIds,
					})),
				})),
			},
			type: DashboardTabType.COLUMNS,
		};

		try {
			const response = await createDashboardTab({
				params: { tab: newColumnsTab },
			});

			if (response.success && response.tab) {
				navigateWithAccount(`columns/${response.tab.key}`);
			}
		} catch (error) {
			handleError(error);
		}

		setAnchorEl(null);
	};

	const handleClickDelete = async () => {
		const confirmPrompt = await showPrompt({
			promptType: "confirm",
			title: "Are you sure you want to delete this tab?",
			description: "Your streams will remain available in the stream manager.",
			confirmButton: { label: "Delete", intent: "destruction" },
		});

		if (confirmPrompt.state === "ok") {
			try {
				const response = await deleteDashboardTab({
					params: { key: tabKey },
				});

				if (response.success) {
					navigateAfterTabDelete(tabKey);
				} else if (response.error) {
					handleError(response.error);
				}
			} catch (error) {
				handleError(error);
			}
		}
		setAnchorEl(null);
	};

	return (
		<>
			<IconButton
				disabled={false}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				<MoreVert fontSize="small" />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				open={Boolean(anchorEl)}
				sx={tabDropdownSx}
			>
				<MenuItem onClick={handleClickRename}>Rename Tab</MenuItem>
				<MenuItem onClick={handleClickDelete}>Delete Tab</MenuItem>
				{columnsTabLimit > 1 && (
					<MenuItem
						onClick={handleSubmitDuplicate}
						disabled={atColumnsTabLimit}
					>
						Duplicate Tab
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

const tabDropdownSx = {
	"& .MuiMenu-list": {
		background: "grey.800",
		width: "195px",
	},
	"& .MuiMenuItem-gutters": {
		fontSize: theme.typography.p2.fontSize,
	},
};
